export default function AvatarOrInitial({ 
  avatar, 
  name, 
  user, // Accept user object as alternative to name
  size = 'md', 
  className = '' 
}) {
  // Use name from user object if name prop is not provided
  // Try multiple fields: display_name is the primary field in family_members table
  const displayName = name || user?.display_name || user?.name;
  
  // Use avatar from user object if avatar prop is not provided
  const avatarToUse = avatar || user?.avatar;
  
  // Debug logging for avatar issues
  if (!displayName) {
    console.log('🖼️ AvatarOrInitial missing name:', { 
      name, 
      user, 
      attempted_fields: {
        name_prop: name,
        user_display_name: user?.display_name,
        user_name: user?.name
      },
      props: { avatar, name, user, size, className } 
    });
  }
  const sizeClasses = {
    xs: 'w-4 h-4 text-xs',
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
    xl: 'w-12 h-12 text-lg',
    '2xl': 'w-16 h-16 text-xl'
  };

  const sizeClass = sizeClasses[size] || sizeClasses.md;

  // Helper function to get consistent color for a name
  const getInitialColor = (name) => {
    if (!name) return 'text-white';
    
    const colors = [
      'text-teal-400',    // Teal
      'text-coral-400',   // Coral  
      'text-gold-400',    // Gold
      'text-magenta-400', // Magenta
      'text-azure-400',    // azure
      'text-emerald-400',   // emerald
      'text-violet-400',  // Violet
      'text-orange-400'   // Orange
    ];
    
    // Use first letter of name to get consistent color
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Priority 1: If avatar is a photo URL, show image
  if (avatarToUse && avatarToUse.match(/^https?:\/\//)) {
    return (
      <div className={`${sizeClass} rounded-full overflow-hidden ${className}`}>
        <img 
          src={avatarToUse} 
          alt={displayName || 'Avatar'} 
          className="w-full h-full object-cover"
          onError={(e) => {
            // If image fails to load, hide it and show fallback
            e.target.style.display = 'none';
            const fallback = e.target.parentElement.querySelector('.fallback-avatar');
            if (fallback) fallback.style.display = 'flex';
          }}
        />
        {/* Hidden fallback that shows if image fails */}
        <div 
          className={`fallback-avatar ${sizeClass} bg-slate-900 rounded-full flex items-center justify-center ${getInitialColor(displayName)} font-semibold absolute inset-0`}
          style={{ display: 'none' }}
        >
          {displayName?.[0]?.toUpperCase() || '?'}
        </div>
      </div>
    );
  }

  // Priority 2: If avatar is an emoji, show emoji (for backward compatibility)
  if (avatarToUse && avatarToUse.length <= 3 && !avatarToUse.match(/^https?:\/\//)) {
    return (
      <div className={`${sizeClass} bg-slate-900 rounded-full flex items-center justify-center font-medium ${className}`}>
        <span className="text-white">{avatarToUse}</span>
      </div>
    );
  }

  // Priority 3: Fallback to slate-900 circle with colored initial
  const initial = displayName?.[0]?.toUpperCase() || '?';
  
  return (
    <div className={`${sizeClass} bg-slate-900 rounded-full flex items-center justify-center ${getInitialColor(displayName)} font-semibold ${className}`}>
      {initial}
    </div>
  );
}